import { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Edit, 
  MapPin, 
  Calendar, 
  Clock, 
  DollarSign, 
  Users, 
  TrendingUp, 
  Briefcase,
  AlertTriangle,
  Star,
  Eye,
  MessageSquare,
  Download,
  Phone,
  Mail
} from 'lucide-react';

import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Progress } from '../../ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Separator } from '../../ui/separator';
import { Project } from './ProjectCard';
import { supabase } from '../../../lib/supabase';
import { DashboardStats } from '../DashboardStats';

interface CandidateWithAnalysis {
  id: string;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
  appliedAt: Date;
  status: 'new' | 'reviewing' | 'interview_scheduled' | 'interviewed' | 'offer_sent' | 'hired' | 'rejected';
  stage: 'application' | 'phone_screen' | 'technical' | 'onsite' | 'final' | 'offer' | 'closed';
  score: {
    technical: number;
    cultural: number;
    experience: number;
    overall: number;
  };
  resume: {
    url: string;
    filename: string;
  };
  lastActivity: Date;
  source: string;
  location?: string;
  experience?: string;
  education?: string;
  skills: string[];
  analysisId?: string;
  verdict?: string;
}

interface ProjectDetailsProps {
  project: Project;
  onBack: () => void;
  onEdit: () => void;
}

export function ProjectDetails({ project, onBack, onEdit }: ProjectDetailsProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [candidates, setCandidates] = useState<CandidateWithAnalysis[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);

  // Fetch candidates for this project
  useEffect(() => {
    const fetchProjectCandidates = async () => {
      try {
        setIsLoadingCandidates(true);
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Fetch project candidates with analysis data
        const { data: projectCandidates, error } = await supabase
          .from('project_candidates')
          .select(`
            *,
            analysis!project_candidates_analysis_id_fkey (
              id,
              fit_score,
              verdict,
              reasoning,
              technical_skills,
              risk_factors,
              created_at
            )
          `)
          .eq('project_id', project.id);

        if (error) {
          console.error('Error fetching project candidates:', error);
          return;
        }

        // Transform the data to match our interface
        const transformedCandidates: CandidateWithAnalysis[] = await Promise.all(
          (projectCandidates || []).map(async (pc) => {
            // Get candidate details from analysis or try to fetch from candidates table
            let candidateName = 'Unknown Candidate';
            let candidateEmail = '';
            let candidatePhone = '';
            let skills: string[] = [];

            if (pc.analysis) {
              // Try to get candidate name from the analysis's candidate record
              const { data: candidateData } = await supabase
                .from('candidates')
                .select('name, email, phone')
                .eq('id', pc.candidate_id)
                .maybeSingle();

              if (candidateData) {
                candidateName = candidateData.name || 'Unknown Candidate';
                candidateEmail = candidateData.email || '';
                candidatePhone = candidateData.phone || '';
              }

                          // Extract skills from technical_skills analysis
            if (pc.analysis.technical_skills && Array.isArray(pc.analysis.technical_skills)) {
              const extractedSkills: string[] = [];
              
              pc.analysis.technical_skills.forEach((ts: any) => {
                let skillName: string | null = null;
                
                if (typeof ts === 'string' && ts.trim().length > 0) {
                  skillName = ts.trim();
                } else if (ts && typeof ts === 'object') {
                  const name = ts.skill || ts.name;
                  if (name && typeof name === 'string' && name.trim().length > 0) {
                    skillName = name.trim();
                  }
                }
                
                if (skillName && skillName !== 'Unknown Skill' && skillName !== 'Parse Error') {
                  extractedSkills.push(skillName);
                }
              });
              
              skills = extractedSkills;
              
              // Log for debugging
              console.log(`Extracted ${skills.length} skills for candidate ${candidateName}:`, skills);
            }
            }

            return {
              id: pc.id,
              name: candidateName,
              email: candidateEmail,
              phone: candidatePhone,
              appliedAt: new Date(pc.created_at),
              status: pc.status as any,
              stage: pc.stage as any,
              score: {
                technical: pc.score_technical || (pc.analysis?.fit_score || 0) * 0.8,
                cultural: pc.score_cultural || (pc.analysis?.fit_score || 0) * 0.9,
                experience: pc.score_experience || (pc.analysis?.fit_score || 0) * 0.85,
                overall: pc.score_overall || pc.analysis?.fit_score || 0
              },
              resume: {
                url: '',
                filename: `${candidateName.replace(/\s+/g, '-').toLowerCase()}-resume.pdf`
              },
              lastActivity: new Date(pc.updated_at || pc.created_at),
              source: pc.source || 'Analysis',
              skills: skills,
              analysisId: pc.analysis?.id,
              verdict: pc.analysis?.verdict
            };
          })
        );

        setCandidates(transformedCandidates);
      } catch (error) {
        console.error('Error fetching project candidates:', error);
      } finally {
        setIsLoadingCandidates(false);
      }
    };

    fetchProjectCandidates();
  }, [project.id]);

  const enhancedStats = {
    applicants: candidates.length,
    interviews: candidates.filter(c => ['interview_scheduled', 'interviewed'].includes(c.status)).length,
    offers: candidates.filter(c => c.status === 'offer_sent').length,
    hires: candidates.filter(c => c.status === 'hired').length,
    avgScore: candidates.length > 0 ? 
      (candidates.reduce((sum, c) => sum + c.score.overall, 0) / candidates.length).toFixed(1) : '0.0'
  };

  const getStatusColor = (status: string) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      active: 'bg-green-100 text-green-800 border-green-200',
      paused: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed: 'bg-blue-100 text-blue-800 border-blue-200',
      archived: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status as keyof typeof colors] || colors.draft;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800 border-gray-200',
      medium: 'bg-blue-100 text-blue-800 border-blue-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      urgent: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[priority as keyof typeof colors] || colors.medium;
  };

  const getStageProgress = (stage: string) => {
    const stages = {
      application: 20,
      phone_screen: 40,
      technical: 60,
      onsite: 80,
      final: 90,
      offer: 95,
      closed: 100
    };
    return stages[stage as keyof typeof stages] || 0;
  };

  const calculateOverallProgress = () => {
    if (candidates.length === 0) return 0;
    const avgProgress = candidates.reduce((sum, c) => sum + getStageProgress(c.stage), 0) / candidates.length;
    return Math.round(avgProgress);
  };

  const isDeadlineWarning = () => {
    const now = new Date();
    const deadline = new Date(project.deadline);
    const daysUntilDeadline = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilDeadline <= 7 && daysUntilDeadline > 0;
  };

  const isOverdue = () => {
    return new Date() > new Date(project.deadline);
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50/50 via-white to-slate-50/30 p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            className="hover:bg-slate-100 rounded-xl"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onEdit}
            className="border-slate-200 hover:bg-slate-50 rounded-xl"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Project
          </Button>
        </div>
      </div>

      {/* Project Header Card */}
      <Card className="bg-gradient-to-r from-white via-slate-50/80 to-white border border-slate-200/60 shadow-xl shadow-slate-200/40 rounded-3xl overflow-hidden">
        <CardContent className="p-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-900/25">
                <Briefcase className="h-8 w-8 text-white" />
              </div>
              <div className="space-y-3">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900 mb-2">{project.title}</h1>
                  <p className="text-lg text-slate-600 font-medium">{project.role}</p>
                  <p className="text-slate-500 mt-2 max-w-2xl leading-relaxed">{project.company} • {project.department}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`${getStatusColor(project.status)} font-medium px-3 py-1 rounded-full border`}>
                    {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                  </Badge>
                  <Badge className={`${getPriorityColor(project.priority)} font-medium px-3 py-1 rounded-full border`}>
                    {project.priority.charAt(0).toUpperCase() + project.priority.slice(1)} Priority
                  </Badge>
                </div>
                {(isDeadlineWarning() || isOverdue()) && (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
                    isOverdue() ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
                  }`}>
                    <AlertTriangle className={`h-4 w-4 ${isOverdue() ? 'text-red-600' : 'text-yellow-600'}`} />
                    <span className={`text-sm font-medium ${isOverdue() ? 'text-red-800' : 'text-yellow-800'}`}>
                      {isOverdue() ? 'Project is overdue!' : 'Deadline approaching soon'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Meta Info */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200/60 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-slate-600" />
              <div>
                <p className="text-sm text-slate-600 font-medium">Location</p>
                <p className="font-semibold text-slate-900">{project.location}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200/60 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-slate-600 font-medium">Budget</p>
                <p className="font-semibold text-slate-900">
                  {project.budget.currency}{project.budget.min.toLocaleString()} - {project.budget.max.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200/60 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-slate-600 font-medium">Created</p>
                <p className="font-semibold text-slate-900">{project.createdAt.toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200/60 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm text-slate-600 font-medium">Deadline</p>
                <p className="font-semibold text-slate-900">
                  {project.deadline.toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-200/60 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="bg-gradient-to-r from-slate-50/90 via-white to-slate-50/90 border-b border-slate-200/60 px-8 py-6">
            <div className="max-w-5xl mx-auto flex items-center justify-center">
              <TabsList className="bg-white/80 backdrop-blur-sm rounded-3xl p-1.5 shadow-lg shadow-slate-200/40 border border-slate-200/60 flex items-center gap-1">
                <TabsTrigger 
                  value="overview" 
                  className="flex items-center gap-2.5 px-6 py-3.5 rounded-2xl data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-slate-900/25 transition-all duration-300 hover:bg-slate-50 text-slate-700 font-medium whitespace-nowrap"
                >
                  <TrendingUp className="h-4 w-4" />
                  <span>Overview</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="candidates" 
                  className="flex items-center gap-2.5 px-6 py-3.5 rounded-2xl data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-slate-900/25 transition-all duration-300 hover:bg-slate-50 text-slate-700 font-medium whitespace-nowrap relative"
                >
                  <Users className="h-4 w-4" />
                  <span>Candidates</span>
                  <div className={`ml-1 px-2 py-0.5 text-xs font-semibold rounded-full min-w-[24px] flex items-center justify-center transition-all duration-300 ${
                    activeTab === 'candidates' 
                      ? 'bg-white/20 text-white' 
                      : 'bg-slate-200 text-slate-700'
                  }`}>
                    {enhancedStats.applicants}
                  </div>
                </TabsTrigger>

                <TabsTrigger 
                  value="team" 
                  className="flex items-center gap-2.5 px-6 py-3.5 rounded-2xl data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-xl data-[state=active]:shadow-slate-900/25 transition-all duration-300 hover:bg-slate-50 text-slate-700 font-medium whitespace-nowrap"
                >
                  <Users className="h-4 w-4" />
                  <span>Team</span>
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          <TabsContent value="overview" className="p-8 space-y-6">
            {/* Project-specific Dashboard Stats */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Project Statistics</h3>
              <DashboardStats projectId={project.id} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Project Requirements</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Experience Required</p>
                      <p className="font-semibold">{project.requirements.experience}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Education Required</p>
                      <p className="font-semibold">{project.requirements.education}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium mb-2">Required Skills</p>
                    <div className="flex flex-wrap gap-2">
                      {project.requirements.skills.map((skill, index) => (
                        <Badge key={index} variant="secondary" className="px-3 py-1 rounded-full">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">Overall Progress</h4>
                      <span className="text-sm font-medium">{calculateOverallProgress()}%</span>
                    </div>
                    <Progress value={calculateOverallProgress()} className="h-3 rounded-full" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Team Members</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {project.team.map((member) => (
                      <div key={member.id} className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={member.avatar} />
                          <AvatarFallback>
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-gray-600">{member.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="candidates" className="p-8 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Project Candidates</h3>
                <p className="text-sm text-slate-600">
                  {isLoadingCandidates ? 'Loading...' : `${candidates.length} candidates analyzed for this project`}
                </p>
              </div>
            </div>
            
            {isLoadingCandidates ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                        <div className="flex-1 space-y-3">
                          <div className="h-5 bg-slate-200 rounded w-1/3"></div>
                          <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                          <div className="flex gap-2">
                            <div className="h-6 bg-slate-200 rounded w-20"></div>
                            <div className="h-6 bg-slate-200 rounded w-16"></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : candidates.length > 0 ? (
              <div className="space-y-4">
                {candidates.map((candidate) => (
                  <Card key={candidate.id} className="hover:shadow-lg transition-all duration-300 border border-slate-200/60 rounded-2xl overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <Avatar className="h-14 w-14 border-2 border-slate-100 shadow-sm">
                            <AvatarImage src={candidate.avatar} />
                            <AvatarFallback className="bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700 font-semibold">
                              {candidate.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 space-y-4">
                            <div>
                              <h4 className="font-bold text-lg text-slate-900">{candidate.name}</h4>
                              <div className="flex items-center gap-4 text-sm text-slate-600 mt-2">
                                {candidate.email && (
                                  <div className="flex items-center gap-1">
                                    <Mail className="h-4 w-4" />
                                    <span>{candidate.email}</span>
                                  </div>
                                )}
                                {candidate.phone && (
                                  <div className="flex items-center gap-1">
                                    <Phone className="h-4 w-4" />
                                    <span>{candidate.phone}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                              <Badge 
                                variant="outline" 
                                className="border-slate-300 text-slate-700 bg-slate-50"
                              >
                                {candidate.status.replace('_', ' ').charAt(0).toUpperCase() + candidate.status.replace('_', ' ').slice(1)}
                              </Badge>
                              
                              <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1">
                                <Star className="h-4 w-4 text-amber-500 fill-current" />
                                <span className="text-sm font-bold text-amber-700">
                                  {typeof candidate.score.overall === 'number' 
                                    ? candidate.score.overall.toFixed(1) 
                                    : candidate.score.overall}
                                </span>
                              </div>

                              {candidate.verdict && (
                                <Badge 
                                  className={
                                    candidate.verdict === 'strong_fit' 
                                      ? 'bg-green-100 text-green-800 border-green-200' 
                                      : candidate.verdict === 'potential_fit'
                                      ? 'bg-blue-100 text-blue-800 border-blue-200'
                                      : candidate.verdict === 'needs_consideration'
                                      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                      : 'bg-red-100 text-red-800 border-red-200'
                                  }
                                >
                                  {candidate.verdict.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </Badge>
                              )}
                            </div>

                            {candidate.skills.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {candidate.skills.slice(0, 6).map((skill, index) => (
                                  <Badge 
                                    key={index} 
                                    variant="outline" 
                                    className="text-xs bg-slate-50 border-slate-200 text-slate-700"
                                  >
                                    {skill}
                                  </Badge>
                                ))}
                                {candidate.skills.length > 6 && (
                                  <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200 text-slate-500">
                                    +{candidate.skills.length - 6} more
                                  </Badge>
                                )}
                              </div>
                            )}

                            {/* Score breakdown */}
                            <div className="grid grid-cols-3 gap-4 pt-2">
                              <div className="text-center">
                                <div className="text-xs text-slate-500 mb-1">Technical</div>
                                <div className="font-semibold text-slate-900">
                                  {typeof candidate.score.technical === 'number' 
                                    ? candidate.score.technical.toFixed(1) 
                                    : candidate.score.technical}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-xs text-slate-500 mb-1">Cultural</div>
                                <div className="font-semibold text-slate-900">
                                  {typeof candidate.score.cultural === 'number' 
                                    ? candidate.score.cultural.toFixed(1) 
                                    : candidate.score.cultural}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-xs text-slate-500 mb-1">Experience</div>
                                <div className="font-semibold text-slate-900">
                                  {typeof candidate.score.experience === 'number' 
                                    ? candidate.score.experience.toFixed(1) 
                                    : candidate.score.experience}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          {candidate.analysisId && (
                            <Button variant="outline" size="sm" className="rounded-xl border-slate-300 hover:bg-slate-50">
                              <Eye className="h-4 w-4 mr-2" />
                              View Analysis
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="rounded-xl hover:bg-slate-50">
                            <Download className="h-4 w-4 mr-2" />
                            Export
                          </Button>
                          <Button variant="default" size="sm" className="rounded-xl bg-slate-900 hover:bg-slate-800">
                            Schedule Interview
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 rounded-3xl">
                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center mb-6 shadow-sm">
                  <Users className="h-12 w-12 text-slate-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">No Candidates Yet</h3>
                <p className="text-slate-600 max-w-md mx-auto">
                  Candidates will appear here automatically when you analyze resumes for this project.
                </p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="team" className="p-8 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Project Team</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {project.team.map((member) => (
                    <Card key={member.id} className="p-4">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16">
                          <AvatarImage src={member.avatar} />
                          <AvatarFallback className="text-lg">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h4 className="font-semibold">{member.name}</h4>
                          <p className="text-sm text-gray-600">{member.role}</p>
                          <div className="flex gap-2 mt-2">
                            <Button variant="outline" size="sm">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              Email
                            </Button>
                            <Button variant="outline" size="sm">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              Chat
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
} 